import React, { useState, useRef, useEffect } from "react";
import { Input, Button, Card, Typography, message, Tooltip, Select } from "antd";
import { CloseCircleOutlined, DownloadOutlined } from "@ant-design/icons";
import { v4 as uuidv4 } from "uuid";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw"
import rehypeSanitize from 'rehype-sanitize'
import remarkBreaks from 'remark-breaks'
import remarkGfm from 'remark-gfm'
import jsPDF from "jspdf"; // add jspdf library for pdf generation
import "./UwFraud.css";
import { useLocation } from "react-router-dom";
import { analyzeRiskAPI } from "../../apis/FinancialFraudAPI";

const LOGIN_TYPE_PRE_LOGIN = "Pre-Login";
const LOGIN_TYPE_LOGGED_IN = "Logged-In";

const LOGIN_TYPE_OPTIONS = [
  { label: "Pre Logged In", value: LOGIN_TYPE_PRE_LOGIN },
  { label: "Logged In", value: LOGIN_TYPE_LOGGED_IN },
];

const MarkdownComponentsResponse = {
  table: ({ children }) => (
    <div style={{ overflowX: 'auto', margin: '16px 0', border: '1px solid #000', borderRadius: '4px' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
        {children}
      </table>
    </div>
  ),
  thead: ({ children }) => (
    <thead style={{ backgroundColor: '#f2f2f2', fontWeight: 'bold', borderBottom: '1px solid #000' }}>
      {children}
    </thead>
  ),
  tr: ({ children }) => (
    <tr style={{ borderBottom: '1px solid #000' }}>
      {children}
    </tr>
  ),
  th: ({ children }) => (
    <th style={{ padding: '8px', borderRight: '1px solid #000' }}>
      {children}
    </th>
  ),
  td: ({ children }) => (
    <td style={{ padding: '8px', borderRight: '1px solid #000' }}>
      {children}
    </td>
  ),
}

export default function UwFraud({ feature }) {
  const [inputValue, setInputValue] = useState("");
  const [applicationNo, setApplicationNo] = useState("");
  const [loginType, setLoginType] = useState(LOGIN_TYPE_PRE_LOGIN);
  const [responseData, setResponseData] = useState("");
  const [loading, setLoading] = useState(false);
  const location = useLocation();
  const inputRef = useRef(null);
  const [inputError, setInputError] = useState("");
  const [applicationNoError, setApplicationNoError] = useState("");

  useEffect(() => {
    inputRef.current?.focus();
  }, [inputValue]);

  // Type dropdown + Application No are financial-flow only; investment flow is untouched
  const isFinancialFlow = feature?.type === "financial";
  const isApplicationNoRequired = isFinancialFlow && loginType === LOGIN_TYPE_LOGGED_IN;

  const isValidInput = (text) => {
    const hasInvalidChars = /[^a-zA-Z0-9\s&.,'-]/.test(text);
    const hasUnderscore = /_/.test(text);
    const hasHtmlTags = /<\/?[\w\s="'/:;#.-]+>/.test(text);
    return !hasInvalidChars && !hasUnderscore && !hasHtmlTags;
  };

  const onChangeHandler = (e) => {
    const rawVal = e.target.value;
    const inputElem = e.target;
    const selectionStart = inputElem.selectionStart;

    // Filter invalid characters (allow letters, digits, space, comma, period)
    let filteredVal = rawVal.replace(/[^a-zA-Z0-9\s,\.]/g, "");

    // Show message if length exceeds 256
    if (filteredVal.length > 256) {
      message.destroy(); // remove previous messages
      message.warning("Input cannot exceed 256 characters");
      filteredVal = filteredVal.slice(0, 256); // then trim
    }

    // Calculate how many characters removed before cursor
    let charsRemovedBeforeCursor = 0;
    for (let i = 0, j = 0; i < rawVal.length && j < filteredVal.length; i++) {
      if (rawVal[i] !== filteredVal[j]) {
        charsRemovedBeforeCursor++;
      } else {
        j++;
      }
      if (i >= selectionStart - 1) break;
    }

    setInputValue(filteredVal);

    // Show error if invalid chars removed
    if (filteredVal !== rawVal) {
      setInputError(
        "Invalid Input"
      );
    } else if (filteredVal.trim() === "") {
      setInputError("Input cannot be empty");
    } else {
      setInputError("");
    }

    // Restore cursor position after filtering
    setTimeout(() => {
      if (inputRef.current) {
        const newPos = selectionStart - charsRemovedBeforeCursor;
        inputRef.current.setSelectionRange(newPos, newPos);
      }
    }, 0);
  };

  // Application No handler - allow alphanumeric and hyphen only, max 50 chars
  const onApplicationNoChangeHandler = (e) => {
    const rawVal = e.target.value;

    // Ignore all whitespace first
    const trimmedInput = rawVal.replace(/\s/g, "");

    // Allow only digits
    let filteredVal = trimmedInput.replace(/[^0-9]/g, "");

    if (filteredVal.length > 13) {
      filteredVal = filteredVal.slice(0, 13);
    }

    setApplicationNo(filteredVal);

    const hasInvalidCharacters = /[^0-9\s]/.test(rawVal);

    if (hasInvalidCharacters) {
      setApplicationNoError("Application No should contain only numbers");
    } else if (
      isApplicationNoRequired &&
      filteredVal.length === 0
    ) {
      setApplicationNoError("Application No is required for Logged In");
    } else if (
      filteredVal.length > 0 &&
      filteredVal.length < 13
    ) {
      setApplicationNoError("Application No must be exactly 13 digits");
    } else {
      setApplicationNoError("");
    }
  };

  const onLoginTypeChangeHandler = (value) => {
    setLoginType(value);

    // Reset the other fields
    setApplicationNo("");
    setInputValue("");

    // Clear validation errors
    setInputError("");

    if (value === LOGIN_TYPE_LOGGED_IN) {
      setApplicationNoError("Application No is required for Logged In");
    } else {
      setApplicationNoError("");
    }

    // Clear previous response
    setResponseData("");
  };

  // Submit is disabled until query is valid, and (for the financial flow only)
  // application no is valid/present when required
  const isSubmitDisabled =
    !inputValue.trim() ||
    !!inputError ||
    (isFinancialFlow && isApplicationNoRequired && !applicationNo.trim()) ||
    (isFinancialFlow && !!applicationNoError);

  const handleSubmit = async () => {
    if (!inputValue.trim()) {
      message.destroy();
      message.warning("Please enter a query before submitting");
      return;
    }

    if (!isValidInput(inputValue)) {
      message.destroy();
      message.error("Invalid input !");
      return;
    }

    if (isApplicationNoRequired && !applicationNo.trim()) {
      message.destroy();
      message.warning("Application No is required for Logged In");
      return;
    }
    if (isApplicationNoRequired && applicationNo.length !== 13) {
      message.destroy();
      message.error("Application No must be exactly 13 digits");
      return;
    }

    setLoading(true);
    setResponseData("");

    try {
      // applicationNo/loginType are only relevant to the financial flow;
      // omit them entirely for investment so nothing new reaches that DB collection
      const data = await analyzeRiskAPI({
        query: inputValue,
        type: feature?.type,
        ...(isFinancialFlow ? { applicationNo, loginType } : {}),
      });

      setResponseData(data?.data?.report || "No report available");
    } catch (err) {
      console.error(err);
      setResponseData("Error: Something went wrong, please try again.");
    } finally {
      setLoading(false);
    }
  };


  // Download responseData as PDF
  const handleDownloadPDF = () => {
    if (!responseData) {
      message.destroy()
      message.warning("No data available to download");
      return;
    }

    const doc = new jsPDF({
      unit: "pt",
      format: "a4",
    });

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 40;
    const maxLineWidth = pageWidth - margin * 2;
    const lineHeight = 16;
    let y = margin;

    const baseFontSize = 10;
    doc.setFont("'Inter', sans-serif");
    doc.setFontSize(baseFontSize);

    function drawWrappedText(text, x, y, fontStyle = "normal", fontSize = baseFontSize) {
      doc.setFont("'Inter', sans-serif", fontStyle);
      doc.setFontSize(fontSize);
      const lines = doc.splitTextToSize(text, maxLineWidth);

      for (const line of lines) {
        if (y > pageHeight - margin) {
          doc.addPage();
          y = margin;
        }
        doc.text(line, x, y);
        y += lineHeight + (fontSize - baseFontSize);
      }
      return y;
    }

    function parseInlineStyles(text) {
      const chunks = [];
      const regex = /(\*\*([^*]+)\*\*|\*([^*]+)\*)/g;
      let lastIndex = 0;
      let match;

      while ((match = regex.exec(text)) !== null) {
        if (match.index > lastIndex) {
          chunks.push({
            text: text.substring(lastIndex, match.index),
            style: "normal",
          });
        }

        if (match[2]) {
          chunks.push({ text: match[2], style: "bold" });
        } else if (match[3]) {
          chunks.push({ text: match[3], style: "italic" });
        }

        lastIndex = match.index + match[0].length;
      }

      if (lastIndex < text.length) {
        chunks.push({
          text: text.substring(lastIndex),
          style: "normal",
        });
      }

      return chunks;
    }

    function drawStyledLine(y, xStart, maxWidth, chunks, fontSize = baseFontSize, lineHeight = 16) {
      let x = xStart;
      const spaceWidth = doc.getTextWidth(" ");

      for (const chunk of chunks) {
        doc.setFont("'Inter', sans-serif", chunk.style);
        doc.setFontSize(fontSize);

        const words = chunk.text.split(" ");

        for (let i = 0; i < words.length; i++) {
          const word = words[i];
          const wordWidth = doc.getTextWidth(word);

          if (x + wordWidth > maxWidth) {
            y += lineHeight;
            if (y > pageHeight - margin) {
              doc.addPage();
              y = margin;
            }
            x = xStart;
          }

          doc.text(word, x, y);
          x += wordWidth;

          if (i !== words.length - 1) {
            x += spaceWidth;
          }
        }
      }

      return y + lineHeight;
    }

    //Clean unwanted color tags, markdown headers, and encoding artifacts
    let cleanedResponse = responseData
      // Remove color labels like "Red*", "Yellow*", "Green*" (case-insensitive)
      .replace(/\b(Red|Yellow|Green)\s*\*/gi, "")
      // Remove any Ø=...* or encoding artifacts
      .replace(/Ø[^A-Za-z0-9\n\r]{0,10}\s*(Red|Yellow|Green)?\*/gi, "")
      .replace(/Ø=.*?\*/g, "")
      // Remove markdown headers (###, #### etc.)
      .replace(/#+\s*/g, "")
      // Remove asterisks used for emphasis (e.g. *word*)
      .replace(/\*/g, "")
      // Remove any stray non-ASCII garbage
      .replace(/[^\x00-\x7F]+/g, " ")

      .trim();

    // Now split cleaned text into lines
    const lines = cleanedResponse.split("\n");



    lines.forEach((line) => {
      let trimmedLine = line.trim();

      if (/^####\s*/.test(trimmedLine)) {
        const text = trimmedLine.replace(/^####\s*/, "");
        y = drawWrappedText(text, margin, y, "bold", baseFontSize + 4);
      } else if (/^###\s*/.test(trimmedLine)) {
        const text = trimmedLine.replace(/^###\s*/, "");
        y = drawWrappedText(text, margin, y, "bold", baseFontSize + 3);
      } else if (/^##\s*/.test(trimmedLine)) {
        const text = trimmedLine.replace(/^##\s*/, "");
        y = drawWrappedText(text, margin, y, "bold", baseFontSize + 2);
      } else {
        const chunks = parseInlineStyles(trimmedLine);
        y = drawStyledLine(y, margin, margin + maxLineWidth, chunks, baseFontSize, lineHeight);
      }
    });
    if (feature.type == "financial") {
      doc.save("Risk_Analysis.pdf");
    } else {
      doc.save("Investment_Risk_Analysis.pdf");
    }
  };

  return (
    <div className="uwfraud-page">
      <div className="heading-divfraud">
        <p className="text5">{feature?.title}</p>
      </div>

      {/* Type dropdown + small Application No field - shown ABOVE the existing search row */}
      <div
        className="uwfraud-type-appno-container"
        style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}
      >
        {/* Login Type dropdown + Application No are financial-flow only; investment flow never renders these */}
        {isFinancialFlow && (
          <>
            <Select
              placeholder="Type"
              value={loginType}
              onChange={onLoginTypeChangeHandler}
              options={LOGIN_TYPE_OPTIONS}
              style={{ width: 200, height: "40px" }}
            />

            <input
              placeholder={isApplicationNoRequired ? " Application No " : " Application No "}
              value={applicationNo}
              onChange={onApplicationNoChangeHandler}
              style={{ width: 240, height: "40px", borderRadius: "6px", marginLeft: "1rem", border: "white" }}
              suffix={applicationNo ? (
                <CloseCircleOutlined
                  onClick={() => {
                    setApplicationNo("");
                    setApplicationNoError(isApplicationNoRequired ? "Application No is required for Logged In" : "");
                  }}
                  style={{ cursor: "pointer", color: "#9CA3AF", }}
                />
              ) : null}
            />

            {applicationNoError && (
              <div style={{ color: "red", fontSize: 12 }}>{applicationNoError}</div>
            )}
          </>
        )}
        <div
          className="uwfraud-search-container"
          style={{ display: "flex", alignItems: "center" }}
        >
          <Input
            ref={inputRef}
            placeholder="Please enter entity details like person name, organisation..."
            value={inputValue}
            onChange={onChangeHandler}
            className="uwfraud-input"
            bordered={false}
            style={{ flex: 1 }}
            suffix={inputValue ? (
              <CloseCircleOutlined
                onClick={() => {
                  setInputValue("");
                  setInputError("");
                }}
                style={{ cursor: "pointer", color: "#9CA3AF" }}
              />
            ) : null}
          />


          {inputError && (
            <div style={{ color: "red", marginTop: 4, fontSize: 12 }}>{inputError}</div>
          )}
        </div>
        <Button
          className="uwfraud-search-btn"
          onClick={handleSubmit}
          loading={loading}
          disabled={isSubmitDisabled}
          style={{ marginLeft: 8, height: "38px" }}
        >
          Submit
        </Button>
      </div>

      <div className="download-button-wrapper">
        <div className="response-text">Analysis</div>
        <Tooltip title="Download PDF">
          <Button className="download-pdf-btn" onClick={handleDownloadPDF}>
            <DownloadOutlined />
          </Button>
        </Tooltip>
      </div>

      <Card
        className="uwfraud-response-card"
        bodyStyle={{
          fontFamily: "'Inter', sans-serif",
          whiteSpace: "wrap",
          overflowY: "auto",
          padding: "12px",
          height: "100%",
          scrollbarWidth: "thin",
        }}
      >
        {responseData ?
          <ReactMarkdown
            remarkPlugins={[remarkGfm, remarkBreaks]}
            rehypePlugins={[rehypeRaw, rehypeSanitize]}
            components={MarkdownComponentsResponse}
          >
            {responseData}
          </ReactMarkdown>
          :
          ""
        }
      </Card>
    </div>
  );
}